import { useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import { IconAlarm, IconChevronLeft, IconFileText, IconList, IconSearch, IconSend } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { DataError, useList } from '@hana/data';
import { Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { PAD_TABS, PHONE_BILLS, PHONE_SEGS, PHONE_SEL, draftRows, finalRows, otherRows, phoneChain, phoneFacts, releaseRows, type PadRow, type PhoneBill, type PhoneSeg } from './compact';
import { norm } from './logic';
import type { BlTab } from './types';
import s from './blCompact.module.css';

// Mint-BL-iPad (danh sách; bấm dòng → trang chi tiết 2:1 ở BlDetailPage) · Mint-BL-Phone (danh sách + chi tiết). Chỉ đọc; nút ghi báo toast.
// Không có tiền: công nợ hiện Có / Không / Vượt (money = hide).

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const timeLike = (t: string) => /^(còn|quá|hạn)\s/i.test(t);

function Right({ text, rc }: { text: string; rc: PadRow['rc'] }) {
  if (!text) return null;
  return (
    <span className={`${s.right} ${rc ? s[rc] : ''}`} title={text}>
      {timeLike(text) && <IconAlarm size={13} aria-hidden="true" />}
      {text.replace(/^(hạn|còn)\s+/, '').replace(/^quá\s+/, '−')}
    </span>
  );
}

function Search({ q, onQ }: { q: string; onQ: (v: string) => void }) {
  return (
    <label className={s.search}>
      <IconSearch size={16} aria-hidden="true" />
      <input aria-label="Tìm" placeholder="Job / HBL / khách" value={q} onChange={(e) => onQ(e.target.value)} />
    </label>
  );
}


export function BlCompact({ phone }: { phone: boolean }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const dr = useList('blDraft', { limit: 5000 });
  const fin = useList('blFinal', { limit: 5000 });
  const rel = useList('blRelease', { limit: 5000 });
  const amd = useList('blAmend', { limit: 5000 });
  const loi = useList('blLoi', { limit: 5000 });
  const orig = useList('blOriginal', { limit: 5000 });
  const mnf = useList('blManifest', { limit: 5000 });
  const [tab, setTab] = useState<BlTab>('draft');
  const [seg, setSeg] = useState<PhoneSeg>('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const forced = readState();
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  const rows = useMemo(() => {
    const d = { amends: amd.data?.items ?? [], lois: loi.data?.items ?? [], originals: orig.data?.items ?? [], manifests: mnf.data?.items ?? [] };
    return {
      draft: draftRows(dr.data?.items ?? []), final: finalRows(fin.data?.items ?? []), rel: releaseRows(rel.data?.items ?? []),
      amd: otherRows('amd', d), loi: otherRows('loi', d), orig: otherRows('orig', d), mnf: otherRows('mnf', d),
    } satisfies Record<BlTab, PadRow[]>;
  }, [dr.data, fin.data, rel.data, amd.data, loi.data, orig.data, mnf.data]);

  const loading = [dr, fin, rel, amd, loi, orig, mnf].some((x) => x.isLoading);
  const failed = [dr, fin, rel, amd, loi, orig, mnf].find((x) => x.isError);
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (loading) state = 'loading';
  else if (failed) state = failed.error instanceof DataError && failed.error.code === 'noperm' ? 'noperm' : 'error';
  const ready = state === 'ready' || state === 'noperm';
  const refetch = () => [dr, fin, rel, amd, loi, orig, mnf].forEach((x) => void x.refetch());

  const match = (code: string, k: string) => !q.trim() || norm(`${code} ${k}`).includes(norm(q.trim()));
  const stateProps = {
    state,
    title: 'Không có B/L khớp',
    desc: 'Kéo xuống để làm mới.',
    icon: <IconFileText size={26} stroke={1.8} />,
    action: state === 'error' ? <Button onClick={refetch}>{t('bl.retry')}</Button> : undefined,
    skeleton: <Skeleton rows={6} />,
  };

  // ---------- Điện thoại ----------
  if (phone) {
    const bills = PHONE_BILLS.filter((b) => (seg === 'all' || b.seg === seg) && match(b.code, b.k));
    const cur = PHONE_BILLS.find((b) => b.id === sel);
    if (cur) return <PhoneDetail b={cur} onBack={() => setSel(null)} onWrite={noWrite} noperm={state === 'noperm'} />;
    return (
      <Page kind="table">
        <PageHeader findPad title="Chứng từ & B/L" />
        <div className={s.page}>
          <Search q={q} onQ={setQ} />
          <Segmented label="Trạng thái" value={seg} onChange={(k) => setSeg(k as PhoneSeg)} items={PHONE_SEGS.map((x) => ({ key: x.key, label: x.label, n: ready ? PHONE_BILLS.filter((b) => x.key === 'all' || b.seg === x.key).length : undefined }))} />
          {state === 'noperm' && <div role="note" className={s.note}>{t('bl.viewOnlyRel')}</div>}
          <StateView {...stateProps} state={ready && bills.length === 0 ? 'empty' : ready ? 'ready' : state} title={ready ? 'Không có B/L khớp' : 'Không có B/L nào'}>
            <ul className={s.cards}>
              {bills.map((b) => (
                <li key={b.id}>
                  <button type="button" className={s.pcard} aria-pressed={b.id === PHONE_SEL} onClick={() => setSel(b.id)}>
                    <span className={s.top}><b className={s.code}>{b.code}</b><span className={`${s.chip} ${s[`t_${b.tone}`]}`}><i aria-hidden="true" />{b.chip}</span></span>
                    <span className={s.k}>{b.k}</span>
                    <span className={s.sub}><span className={s.ell}>{b.sub}</span><Right text={b.right} rc={b.rc} /></span>
                  </button>
                </li>
              ))}
            </ul>
          </StateView>
        </div>
      </Page>
    );
  }

  // ---------- iPad ----------
  const list = rows[tab].filter((r) => match(r.code, r.k));
  const count = (k: BlTab) => (k === 'amd' ? rows.amd.filter((r) => r.chip === 'Chờ Trưởng CS').length : k === 'draft' ? rows.draft.filter((r) => r.chip !== 'Đã xác nhận' && !r.right.startsWith('Đã')).length : k === 'final' ? rows.final.filter((r) => r.chip !== 'Đã gửi').length : rows[k].length);
  return (
    <Page kind="table">
      <PageHeader findPad title="Chứng từ & B/L" />
      <div className={s.page}>
        <div className={s.tabsRow}>
          <Segmented label="Nhóm việc B/L" value={tab} onChange={(k) => { setTab(k as BlTab); }} items={PAD_TABS.map((x) => ({ key: x.key, label: x.label, n: ready ? count(x.key) : undefined }))} />
        </div>
        {state === 'noperm' && <div role="note" className={s.note}>{t('bl.viewOnlyRole')}</div>}
        <section className={s.pad} aria-label={PAD_TABS.find((x) => x.key === tab)?.label}>
          <div className={s.filters}><Search q={q} onQ={setQ} /></div>
          <StateView {...stateProps} state={ready && list.length === 0 ? 'empty' : ready ? 'ready' : state}>
            <ul className={s.rows}>
              {list.map((r) => (
                <li key={r.id}>
                  <button type="button" className={s.row} onClick={() => void navigate({ to: '/ezy/bl/$id', params: { id: r.id } })}>
                    <span className={s.top}><b className={s.link}>{r.code}</b><Right text={r.right} rc={r.rc} /></span>
                    <span className={s.ell}>{r.k}</span>
                    <span className={s.meta}><span className={`${s.pill} ${s[`t_${r.tone}`]}`}>{r.chip}</span>{r.sub}</span>
                  </button>
                </li>
              ))}
            </ul>
          </StateView>
        </section>
      </div>
    </Page>
  );
}

function PhoneDetail({ b, onBack, onWrite, noperm }: { b: PhoneBill; onBack: () => void; onWrite: () => void; noperm: boolean }) {
  const t = useT();
  const facts = phoneFacts(b);
  const rel = b.seg === 'rel';
  const act = rel ? (b.step === 4 ? 'Gửi Release Confirmation' : b.mine && b.step === 2 ? (noperm ? 'Không có quyền duyệt' : 'Xem công nợ & duyệt') : 'Chờ điều kiện kỹ thuật') : b.seg === 'draft' ? (b.id === 'd1' ? 'Thiếu LOI · không gửi được' : 'Nhắc khách xác nhận') : b.seg === 'final' ? 'Gửi Final B/L' : b.signed ? 'Đã ký nhận' : 'Ký nhận giao chứng từ';
  const off = noperm || act.startsWith('Thiếu') || act.startsWith('Chờ ') || act === 'Đã ký nhận';
  return (
    <Page kind="table">
      <PageHeader findPad title={b.seg === 'orig' ? (b.no ?? b.code) : b.code} actions={<span className={`${s.chip} ${s[`t_${b.tone}`]}`}>{b.chip}</span>} />
      <div className={s.page}>
        <button type="button" className={s.back} aria-label="Quay lại danh sách" onClick={onBack}><IconChevronLeft size={22} aria-hidden="true" /></button>
        <div className={s.dsub}>{b.job} · {b.k}</div>
        <section className={s.facts}>
          {facts.map(([k, v, bad]) => <div key={k}><span>{k}</span><b className={bad ? s.err : ''}>{v}</b></div>)}
        </section>
        {rel && (
          <section aria-label="Luồng duyệt" className={s.chain}>
            <b>{t('bl.flow')}</b>
            {phoneChain(b).map((c) => (
              <div key={c.k} className={`${s.step} ${s[`s_${c.state}`]}`}><i>{c.i}</i><div><div>{c.k}</div><small>{c.sub}</small></div></div>
            ))}
          </section>
        )}
        {rel && b.missing && b.missing.length > 0 && <div role="note" className={s.miss}><b>{t('bl.missing')}</b> {b.missing.join(' · ')}</div>}
        <div className={s.two}>
          <Button icon={<IconList size={16} aria-hidden="true" />} onClick={onWrite}>Holding List shipper</Button>
          <Button icon={<IconSend size={16} aria-hidden="true" />} onClick={onWrite}>{t('bl.sendSno')}</Button>
        </div>
        <Button variant="primary" disabled={off} onClick={onWrite} className={s.cta}>{act}</Button>
      </div>
    </Page>
  );
}
